import type { TFunction } from 'i18next';

import type { Rules } from '@/api/teamCategories';

// validateRulesClient mirrors the DB CHECK constraints + service
// validation so the user gets a specific, field-named issue list at
// save time instead of the generic `invalid_input` / `internal_error`
// codes the backend returns when a constraint fires. Kept in sync with
// db/migrations/20260519112628_members_scheduling_rules.sql and
// internal/services/teamcategory/rules.go:validateRules.
//
// Each returned string is already localized — the caller only has to
// render the array.
export function validateRulesClient(
  rules: Rules,
  t: TFunction<'common'>,
): string[] {
  const issues: string[] = [];

  const envelope = (
    label: string,
    target: number | null,
    min: number | null,
    max: number | null,
    bounds: { min: number; max: number },
  ) => {
    if (min !== null && (min < bounds.min || min > bounds.max)) {
      issues.push(
        t('rules.validation.outOfRange', { label, min: bounds.min, max: bounds.max }),
      );
    }
    if (max !== null && (max < bounds.min || max > bounds.max)) {
      issues.push(
        t('rules.validation.outOfRange', { label, min: bounds.min, max: bounds.max }),
      );
    }
    if (target !== null && (target < bounds.min || target > bounds.max)) {
      issues.push(
        t('rules.validation.outOfRange', { label, min: bounds.min, max: bounds.max }),
      );
    }
    if (min !== null && max !== null && max < min) {
      issues.push(t('rules.validation.envelopeMinMax', { label }));
    }
    if (target !== null && min !== null && target < min) {
      issues.push(t('rules.validation.envelopeMinTarget', { label }));
    }
    if (target !== null && max !== null && target > max) {
      issues.push(t('rules.validation.envelopeTargetMax', { label }));
    }
  };

  envelope(
    t('rules.sessionsPerWeek'),
    rules.sessions_per_week_target,
    rules.sessions_per_week_min,
    rules.sessions_per_week_max,
    { min: 0, max: 14 },
  );
  envelope(
    t('rules.sessionDuration'),
    rules.session_duration_minutes_target,
    rules.session_duration_minutes_min,
    rules.session_duration_minutes_max,
    { min: 0, max: 600 },
  );

  const bound = (
    label: string,
    value: number | null,
    min: number,
    max: number,
  ) => {
    if (value !== null && (value < min || value > max)) {
      issues.push(t('rules.validation.outOfRange', { label, min, max }));
    }
  };
  bound(t('rules.minLeadersPresent'), rules.min_leaders_present, 0, 10);

  // Time window — DB requires end strictly after start when both set.
  if (
    rules.time_window_start &&
    rules.time_window_end &&
    rules.time_window_end <= rules.time_window_start
  ) {
    issues.push(t('rules.validation.timeOrdering'));
  }

  // Location fraction — min denominator must be ≥ preferred (= a share
  // that is not larger than preferred).
  if (
    rules.preferred_location_fraction_denominator !== null &&
    rules.min_location_fraction_denominator !== null &&
    rules.min_location_fraction_denominator < rules.preferred_location_fraction_denominator
  ) {
    issues.push(t('rules.validation.fractionEnvelope'));
  }

  return issues;
}
